import { z } from "zod";
import { optionalEmail, text } from "./common";

/**
 * The product's own name, logo and support contacts. Set once in Platform admin → Branding; every screen,
 * email, invitation and printout reads it. DEFAULT_PLATFORM_BRANDING is the only place the default name is written.
 */
export interface PlatformBranding {
  productName: string;
  shortName: string;
  supportEmail: string | null;
  supportPhone: string | null;
  /** data:image/... URL, or null to use the built-in mark. */
  logoUrl: string | null;
  iconUrl: string | null;
}

export const DEFAULT_PLATFORM_BRANDING: PlatformBranding = {
  productName: "DentOS",
  shortName: "DentOS",
  supportEmail: null,
  supportPhone: null,
  logoUrl: null,
  iconUrl: null,
};

/** Small inline images only (they are sent to every visitor of the sign-in page). */
export const BRANDING_LOGO_MAX = 400_000;
export const BRANDING_ICON_MAX = 140_000;
const imageDataUrl = (max: number) =>
  z
    .string()
    .max(max, "The image is too large. Use a smaller picture (under 250 KB).")
    .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/, "Use a PNG, JPEG or WebP image")
    .nullable();

export const platformBrandingInput = z.object({
  productName: text(60),
  shortName: text(20),
  supportEmail: optionalEmail,
  supportPhone: z.string().trim().max(30).nullable().optional().transform((v) => v || null),
  logoUrl: imageDataUrl(BRANDING_LOGO_MAX),
  iconUrl: imageDataUrl(BRANDING_ICON_MAX),
});
export type PlatformBrandingInput = z.infer<typeof platformBrandingInput>;

/** Which edition a clinic uses. Menus, wording and specialty screens follow it. */
export const SPECIALTIES = ["dental", "ayurveda"] as const;
export type Specialty = (typeof SPECIALTIES)[number];
export const specialtySchema = z.enum(SPECIALTIES);
export const SPECIALTY_LABEL: Record<Specialty, string> = { dental: "Dental", ayurveda: "Ayurveda" };
